/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation } from 'react-query'

import {
  reprocessElasticsearchProject,
  reprocessObservabilityProject,
  reprocessSecurityProject,
} from '@modules/project-admin-api/v1/callers'
import type { ProjectID } from '@modules/project-admin-api/v1/types'
import type { QueryFunctionReturnType } from '@modules/query/types'
import type { ProjectType } from '@modules/ui-types/projects'

/**
 * Returns the appropriate hook for the provided project type.
 */
export function getReprocessProjectMutationHook(
  projectType: ProjectType,
):
  | typeof useReprocessElasticsearchProjectMutation
  | typeof useReprocessObservabilityProjectMutation
  | typeof useReprocessSecurityProjectMutation {
  switch (projectType) {
    case 'observability':
      return useReprocessObservabilityProjectMutation
    case 'security':
      return useReprocessSecurityProjectMutation
    default:
      return useReprocessElasticsearchProjectMutation
  }
}

const useReprocessElasticsearchProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof reprocessElasticsearchProject>,
    unknown,
    { id: ProjectID }
  >({
    mutationFn: ({ id }) =>
      reprocessElasticsearchProject({
        pathParameters: { id },
      }),
  })

const useReprocessObservabilityProjectMutation = () =>
  useMutation<
    QueryFunctionReturnType<typeof reprocessObservabilityProject>,
    unknown,
    { id: ProjectID }
  >({
    mutationFn: ({ id }) =>
      reprocessObservabilityProject({
        pathParameters: { id },
      }),
  })

const useReprocessSecurityProjectMutation = () =>
  useMutation<QueryFunctionReturnType<typeof reprocessSecurityProject>, unknown, { id: ProjectID }>(
    {
      mutationFn: ({ id }) =>
        reprocessSecurityProject({
          pathParameters: { id },
        }),
    },
  )
